import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { homeFor } from "@/lib/roles";
import { Loader2, Eye, EyeOff } from "lucide-react";

const BG = "https://static.prod-images.emergentagent.com/jobs/62c835a5-706c-4400-b80a-b846f14c72ac/images/921ef76a8dc89cfd6a3aa9146d5f18bf424fa93fc862bce14906b54cb780ed5d.jpeg";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate(homeFor(data.role));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      className="relative min-h-screen w-full flex items-center justify-center px-4 py-10 bg-black bg-cover bg-center"
      style={{ backgroundImage: `url(${BG})` }}
    >
      {/* cinematic darkening overlays */}
      <div className="absolute inset-0 bg-black/55" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,0.9) 100%)" }} />

      {/* top-left brand */}
      <div className="absolute top-6 left-6 sm:top-8 sm:left-10 flex items-center gap-2.5 z-10">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FFD700] to-[#C2410C] text-black flex items-center justify-center font-extrabold text-xl shadow-lg">R</div>
        <span className="text-white font-semibold tracking-wide text-sm sm:text-base drop-shadow">THE RANKERS ACADEMY</span>
      </div>

      {/* glass card */}
      <div className="relative z-10 w-full max-w-[440px] rounded-2xl bg-black/70 backdrop-blur-xl border border-white/10 shadow-2xl px-7 sm:px-12 py-10 sm:py-12 animate-[fadeUp_.5s_ease]">
        <h1 className="text-white text-3xl sm:text-4xl font-bold tracking-tight">Sign In</h1>
        <p className="text-white/55 text-sm mt-2 mb-7">Operations &amp; Growth Command Center</p>

        {error && (
          <div className="mb-5 text-sm text-[#FFB4AB] bg-[#7F1D1D]/40 border border-[#B91C1C]/50 rounded-lg px-3 py-2.5" data-testid="login-error">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <input
            data-testid="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            autoComplete="username"
            className="w-full rounded-lg bg-white/[0.08] border border-white/15 px-4 py-3.5 text-[15px] text-white placeholder-white/45 focus:outline-none focus:border-[#FFD700] focus:bg-white/[0.12] transition-colors"
            required
          />
          <div className="relative">
            <input
              data-testid="login-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoComplete="current-password"
              className="w-full rounded-lg bg-white/[0.08] border border-white/15 px-4 py-3.5 pr-11 text-[15px] text-white placeholder-white/45 focus:outline-none focus:border-[#FFD700] focus:bg-white/[0.12] transition-colors"
              required
            />
            <button
              type="button"
              data-testid="toggle-password-visibility"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/45 hover:text-[#FFD700] focus:outline-none transition-colors"
            >
              {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
            </button>
          </div>

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full py-3.5 rounded-lg text-[15px] font-bold text-black bg-gradient-to-r from-[#FFD700] to-[#F59E0B] hover:from-[#FFDF33] hover:to-[#FBB23E] transition-all flex items-center justify-center gap-2 disabled:opacity-60 shadow-lg shadow-[#F59E0B]/20"
          >
            {loading && <Loader2 size={17} className="animate-spin" />}
            {loading ? "Signing in…" : "Sign In"}
          </button>

          <div className="flex justify-end">
            <button type="button" onClick={() => navigate("/forgot-password")} data-testid="forgot-password-link" className="text-xs text-white/55 hover:text-white transition-colors">
              Forgot password?
            </button>
          </div>
        </form>

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-white/15" />
          <span className="text-xs text-white/40">or</span>
          <div className="flex-1 h-px bg-white/15" />
        </div>

        <button
          data-testid="google-login"
          onClick={google}
          className="w-full flex items-center justify-center gap-2.5 border border-white/15 bg-white/[0.06] text-white py-3 rounded-lg text-sm font-medium hover:bg-white/[0.12] transition-colors"
        >
          <svg width="17" height="17" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.6 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.6 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z"/><path fill="#4CAF50" d="M24 43.5c5.5 0 10.3-1.9 13.7-5.1l-6.3-5.3C29.3 34.8 26.8 36 24 36c-5.3 0-9.7-3.1-11.3-7.9l-6.5 5C9.6 39 16.2 43.5 24 43.5z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.1-2.2 3.9-4 5.1l6.3 5.3c-.4.4 6.9-5 6.9-14.4 0-1.2-.1-2.3-.4-3.5z"/></svg>
          Continue with Google
        </button>

        <p className="text-[11px] text-white/35 mt-7 text-center leading-relaxed">
          Restricted access — Staff &amp; Faculty only · Nagpur
        </p>
      </div>

      <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}
